import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';

import intelligenceStudioApi from '../../../api/intelligenceStudioApi';

const normalizeTrust = s => {
  const v = String(s || '').toLowerCase();
  if (v === 'reading') return 'processing';
  return v;
};

const normalizeResponse = resp => {
  const outer = resp || {};
  const okOuter = outer && outer.success !== false;
  const inner = outer?.data || null;
  const okInner = inner && inner.success !== false;
  return { ok: Boolean(okOuter && okInner), outer, inner };
};

const toConflictItems = inner => {
  const contradictions = Array.isArray(inner?.contradictions) ? inner.contradictions : [];
  const overlappingObligations = Array.isArray(inner?.overlappingObligations)
    ? inner.overlappingObligations
    : [];
  const conflictingRelationships = Array.isArray(inner?.conflictingRelationships)
    ? inner.conflictingRelationships
    : [];

  const items = [];

  for (let i = 0; i < contradictions.length; i++) {
    const c = contradictions[i] || {};
    const aDoc = c?.clause1?.doc || 'Document A';
    const bDoc = c?.clause2?.doc || 'Document B';
    const aTitle = c?.clause1?.title || 'Clause';
    const bTitle = c?.clause2?.title || 'Clause';
    items.push({
      key: `contradiction:${i}:${aDoc}:${bDoc}`,
      kind: 'contradiction',
      confidence: 'High',
      title: `${aDoc} vs ${bDoc}`,
      description: `${aTitle} conflicts with ${bTitle}.`,
      evidencePrompt: `Show me evidence (with citations and exact quotes) for the contradiction between "${aDoc}" and "${bDoc}". Focus on "${aTitle}" vs "${bTitle}".`,
    });
  }

  for (let i = 0; i < overlappingObligations.length; i++) {
    const o = overlappingObligations[i] || {};
    const entity = o?.entity || 'Entity';
    const o1 = o?.obligation1 || 'Obligation A';
    const o2 = o?.obligation2 || 'Obligation B';
    items.push({
      key: `overlap:${i}:${entity}`,
      kind: 'potential',
      confidence: 'Medium',
      title: `Potential overlap: ${entity}`,
      description: `${o1} vs ${o2}`,
      evidencePrompt: `Show me evidence (with citations) for obligations involving "${entity}". Compare these obligations and explain if they conflict: "${o1}" vs "${o2}".`,
    });
  }

  for (let i = 0; i < conflictingRelationships.length; i++) {
    const r = conflictingRelationships[i] || {};
    const supporter = r?.supporter || 'Supporter';
    const contradictor = r?.contradictor || 'Contradictor';
    const target = r?.target || 'Target';
    items.push({
      key: `rel:${i}:${supporter}:${contradictor}:${target}`,
      kind: 'potential',
      confidence: 'Medium',
      title: `Conflicting relationship signals`,
      description: `${supporter} supports ${target}, while ${contradictor} contradicts it.`,
      evidencePrompt: `Show me evidence (with citations) for conflicting relationship signals: "${supporter}" supports ${target}, while "${contradictor}" contradicts it.`,
    });
  }

  return {
    contradictionsCount: contradictions.length,
    totalConflicts: typeof inner?.totalConflicts === 'number' ? inner.totalConflicts : items.length,
    items,
  };
};

export default function ContradictionsAutoSurface({
  notebookId,
  notebookTrustState,
  onQuickAsk,
  fileCount = 0,
}) {
  const trust = normalizeTrust(notebookTrustState);
  const [status, setStatus] = useState('idle'); // idle | running | ready | failed
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const prevTrustRef = useRef(trust);
  const lastAutoScanAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const canAutoScan = trust === 'trusted';
  const MIN_AUTO_SCAN_INTERVAL_MS = 5 * 60 * 1000;

  const runScan = useCallback(
    async ({ source }) => {
      if (!notebookId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      setStatus('running');
      setError(null);

      try {
        const resp = await intelligenceStudioApi.findConflicts(notebookId);
        const normalized = normalizeResponse(resp);
        if (!normalized.ok) {
          const msg =
            normalized?.inner?.error || normalized?.outer?.error || 'Contradiction scan failed.';
          setStatus('failed');
          setError(String(msg));
          return;
        }

        const parsed = toConflictItems(normalized.inner);
        setResult({ ...parsed, engine: 'GraphQueryAPI.findConflictsAcrossDocuments' });
        setStatus('ready');

        if (source === 'auto') {
          lastAutoScanAtRef.current = Date.now();
        }
      } catch (e) {
        setStatus('failed');
        setError(String(e?.message || 'Contradiction scan failed.'));
      } finally {
        inFlightRef.current = false;
      }
    },
    [notebookId]
  );

  // If we mount while already Trusted, run a single auto-scan (rate-limited).
  useEffect(() => {
    if (!canAutoScan) return;
    if (status !== 'idle') return;
    const now = Date.now();
    if (now - lastAutoScanAtRef.current < MIN_AUTO_SCAN_INTERVAL_MS) return;
    runScan({ source: 'auto' });
  }, [canAutoScan, status, runScan]);

  // Auto-scan when the vault becomes Trusted (rate-limited).
  useEffect(() => {
    const prev = prevTrustRef.current;
    prevTrustRef.current = trust;

    const becameTrusted = trust === 'trusted' && prev !== 'trusted';
    if (!becameTrusted) return;
    if (!canAutoScan) return;

    const now = Date.now();
    if (now - lastAutoScanAtRef.current < MIN_AUTO_SCAN_INTERVAL_MS) return;

    runScan({ source: 'auto' });
  }, [trust, canAutoScan, runScan]);

  const summary = useMemo(() => {
    if (status !== 'ready') return null;
    return result || null;
  }, [status, result]);

  if (fileCount < 2) {
    return null;
  }
  if (trust !== 'trusted') {
    return null;
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4 text-sm text-slate-700 dark:text-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
            <span className="text-base">🧾</span>
            <span className="truncate">Cross-document consistency check</span>
          </div>
          <div className="mt-1 text-[12px] text-slate-500 dark:text-neutral-400">
            Looks for conflicts or overlapping obligations across documents.
          </div>
        </div>

        <button
          type="button"
          onClick={() => runScan({ source: 'manual' })}
          className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
          title="Run contradiction scan"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${status === 'running' ? 'animate-spin' : ''}`} />
          <span>{status === 'running' ? 'Checking…' : 'Check'}</span>
        </button>
      </div>

      {status === 'idle' && (
        <div className="mt-3 text-[12px] text-slate-600 dark:text-neutral-300">
          This check runs automatically when the vault is ready. You can run it again anytime.
        </div>
      )}

      {status === 'running' && (
        <div className="mt-3 flex items-center gap-2 text-[12px] text-amber-800 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4" />
          <span>Checking for conflicts across documents…</span>
        </div>
      )}

      {status === 'failed' && (
        <div className="mt-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-[12px] text-red-800 dark:text-red-200">
          <div className="font-semibold">Consistency check unavailable</div>
          <div className="opacity-90 mt-1">
            {error || 'The background service is temporarily unavailable.'}
          </div>
        </div>
      )}

      {status === 'ready' && summary && (
        <div className="mt-3 space-y-2">
          {summary.totalConflicts === 0 ? (
            <div className="flex items-center gap-2 text-[12px] text-emerald-800 dark:text-emerald-200">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-semibold">No conflicts detected.</span>
              <span className="opacity-90">Everything looks consistent so far.</span>
            </div>
          ) : (
            <div className="text-[12px] text-slate-600 dark:text-neutral-300">
              <span className="font-semibold text-slate-900 dark:text-white">
                Conflicts found: {summary.contradictionsCount}
              </span>
              {summary.totalConflicts > summary.contradictionsCount ? (
                <span className="opacity-90">
                  {' '}
                  (plus {summary.totalConflicts - summary.contradictionsCount} potential conflicts)
                </span>
              ) : null}
            </div>
          )}

          {summary.items.slice(0, 6).map(it => (
            <div
              key={it.key}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 dark:text-white truncate">
                    {it.title}
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-600 dark:text-neutral-300">
                    {it.description}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400 dark:text-neutral-500">
                    Confidence: <span className="font-semibold">{it.confidence}</span>
                    {it.kind === 'potential' ? (
                      <span className="opacity-80"> (potential)</span>
                    ) : null}
                  </div>
                </div>

                {onQuickAsk && it.evidencePrompt && (
                  <button
                    type="button"
                    onClick={() => onQuickAsk(String(it.evidencePrompt || '').trim())}
                    className="shrink-0 px-2.5 py-1 text-[11px] font-semibold rounded-full bg-white dark:bg-neutral-900/30 border border-slate-200 dark:border-neutral-700 hover:bg-slate-50 dark:hover:bg-neutral-900/50 transition-colors"
                    title={it.evidencePrompt}
                  >
                    Show evidence
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
